/**
 * 科目管理 API（subject-course-sku:SC-01~SC-17 前端）。
 *
 * 对齐后端 product-catalog 模块 REST 契约：
 * - GET   /subjects         -> Paginated<SubjectItem>（筛选 name/code/status + 分页）
 * - GET   /subjects/:id     -> SubjectItem
 * - POST  /subjects         -> SubjectItem（新建，code 机构内唯一）
 * - PATCH /subjects/:id     -> SubjectItem（编辑，code 不可改，携带 version）
 * - POST  /subjects/:id/disable -> SubjectItem（停用，幂等）
 * - POST  /subjects/:id/enable  -> SubjectItem（启用，幂等）
 *
 * 路径从 api.config.ts 导入，禁止内联；Transport 统一解包 `{success, data}` 信封。
 */
import { ApiPaths } from './api.config';
import { http } from './transport';
import type { Paginated } from './campuses.api';

/** 科目状态。 */
export type SubjectStatus = 'ACTIVE' | 'DISABLED';

/** 派生分数段区间项（服务端权威派生，前端不自行硬编码，subject-score-bands-and-scores）。 */
export interface DerivedScoreBand {
  /** 档区间下限（含）。 */
  min: number;
  /** 档区间上限（含）。 */
  max: number;
  /** 展示文案（如「72-83 分」/「108 分及以上」/「71 分以下」）。 */
  label: string;
}

/** 科目视图（列表项与详情共用，含关联 SKU 数派生统计）。 */
export interface SubjectItem {
  id: string;
  organizationId: string;
  /** 科目编码（机构内唯一，创建后不可改） */
  code: string;
  /** 科目名称 */
  name: string;
  status: SubjectStatus;
  /** 科目满分（正整数 1~1000，缺省 100；分数段按此派生） */
  fullScore: number;
  /** 自定义分数段边界点（升序正整数；null = 按默认比例 60/70/80/90% 派生） */
  scoreBandEdges: number[] | null;
  /** 派生分数段区间（服务端按当前满分派生的最终档位，含两端与文案） */
  derivedScoreBands: DerivedScoreBand[];
  /** 关联 SKU 数（后端按 course_skus 表 COUNT 实时派生） */
  skuCount: number;
  /** 乐观锁版本号 */
  version: number;
  /** 更新时间（ISO） */
  updatedAt: string;
}

/** 科目列表查询参数。 */
export interface SubjectListQuery {
  name?: string;
  code?: string;
  status?: SubjectStatus;
  page?: number;
  pageSize?: number;
}

/** 新建科目请求体（fullScore 选填，缺省 100；scoreBandEdges 选填，缺省默认派生）。 */
export interface CreateSubject {
  code: string;
  name: string;
  fullScore?: number;
  /** 自定义分数段边界点（升序正整数 1~满分-1，最多 11 个；null/缺省按默认比例派生） */
  scoreBandEdges?: number[] | null;
}

/** 编辑科目请求体（无 code 字段，携带乐观锁 version；fullScore/scoreBandEdges 可改）。 */
export interface UpdateSubject {
  name?: string;
  fullScore?: number;
  /** 自定义分数段边界点（以生效后满分为基准合并校验；null 清除恢复默认派生） */
  scoreBandEdges?: number[] | null;
  /** 确认强推：调低满分存在超限分数记录时必带（409 信封含 affectedCount），同事务截断 */
  confirm?: boolean;
  version: number;
}

/** 构造带筛选与分页的科目列表查询串。 */
function buildListQuery(query: SubjectListQuery): string {
  const params = new URLSearchParams();
  if (query.name) params.set('name', query.name);
  if (query.code) params.set('code', query.code);
  if (query.status) params.set('status', query.status);
  if (query.page) params.set('page', String(query.page));
  if (query.pageSize) params.set('pageSize', String(query.pageSize));
  const qs = params.toString();
  return qs ? `${ApiPaths.subjects.list}?${qs}` : ApiPaths.subjects.list;
}

async function listSubjects(query: SubjectListQuery = {}): Promise<Paginated<SubjectItem>> {
  return http.get<Paginated<SubjectItem>>(buildListQuery(query));
}

async function getSubject(id: string): Promise<SubjectItem> {
  return http.get<SubjectItem>(ApiPaths.subjects.detail(id));
}

async function createSubject(dto: CreateSubject): Promise<SubjectItem> {
  return http.post<SubjectItem>(ApiPaths.subjects.create, dto);
}

async function updateSubject(id: string, dto: UpdateSubject): Promise<SubjectItem> {
  return http.patch<SubjectItem>(ApiPaths.subjects.update(id), dto);
}

async function disableSubject(id: string): Promise<SubjectItem> {
  return http.post<SubjectItem>(ApiPaths.subjects.disable(id), {});
}

async function enableSubject(id: string): Promise<SubjectItem> {
  return http.post<SubjectItem>(ApiPaths.subjects.enable(id), {});
}

/** 科目 API 唯一对外入口。 */
export const subjectsApi = {
  listSubjects,
  getSubject,
  createSubject,
  updateSubject,
  disableSubject,
  enableSubject,
};
